"use client";

import { FunctionComponent, PropsWithChildren, ReactNode, useEffect, useRef, useState } from "react";
import { Body, Content, Input, Wrapper } from "./autocomplete.styles";

export interface AutocompleteProps {
    /**
     * The input field which will control the autocomplete.
     */
    input: ReactNode;

    /**
     * Whether the autocomplete should be open or nor.
     */
    open: boolean;

    /**
     * Whether out of the box styls of the autocomplete should be used or not.
     */
    useCustomStyles?: boolean;

    /**
     * Will set the autocomplete with to the with of the input.
     */
    fitWidth?: boolean;

    /**
     * How the focus state of focusable elements (buttons, links, etc.) should be work.
     */
    focusMode?: "focus" | "hover";

    /**
     * The css class on keyboard navigation (ArrowDown/ArrowUp).
     */
    hoverClass?: string;

    /**
     * A callback function which will be fired on hover focusable elements by keyboard navigation (ArrowDown/ArrowUp).
     */
    onHoverFocusableElement?: (element: Element) => void;

    /**
     * A callback function which will be fired on click enter during keyboard navigation.
     */
    onClickEnter?: (element: Element) => void;

    /**
     * Wheter the autocomplete should be closed on click a focusable element (buttons, links, etc.).
     */
    hideOnClickFocusableElement?: boolean;
}

export const Autocomplete: FunctionComponent<PropsWithChildren<AutocompleteProps>> = ({
    input,
    open,
    useCustomStyles = false,
    fitWidth = true,
    focusMode = "hover",
    hoverClass,
    onHoverFocusableElement,
    onClickEnter,
    hideOnClickFocusableElement = true,
    children,
}) => {
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [inputWidth, setInputWidth] = useState<number>(0);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLDivElement>(null);
    const isOpen = open || isFocused;

    useEffect(() => {
        if (inputRef.current) {
            const firstChild = inputRef.current.firstElementChild as HTMLElement | null;
            if (firstChild) {
                setInputWidth(firstChild.clientWidth || 0);
            }
        }
    }, [input]);

    useEffect(() => {
        const updateWidth = () => {
            if (inputRef.current) {
                setInputWidth(inputRef.current.parentElement?.offsetWidth || 0);
            }
        };
        const resizeObserver = new ResizeObserver(() => updateWidth());
        const mutationObserver = new MutationObserver(() => updateWidth());
        if (inputRef.current) {
            updateWidth();
            resizeObserver.observe(inputRef.current);
            mutationObserver.observe(inputRef.current, { attributes: true, childList: true, subtree: true });
        }

        return function cleanup() {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setIsFocused(false);
            }
        };

        document.addEventListener("keydown", handleEscape);

        return function cleanup() {
            document.removeEventListener("keydown", handleEscape);
        };
    }, []);

    useEffect(() => {
        if (!isOpen || !focusMode) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== "ArrowDown" && event.key !== "ArrowUp" && event.key !== "Enter") return;
            if (event.key === "Enter" && focusMode === "focus") {
                return;
            }
            event.preventDefault();
            const focusableElements = contentRef.current?.querySelectorAll<HTMLElement>(
                "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
            );
            if (!focusableElements || focusableElements.length === 0) return;
            const currentIndex =
                focusMode === "focus"
                    ? Array.from(focusableElements).indexOf(document.activeElement as HTMLElement)
                    : focusMode === "hover"
                    ? Array.from(focusableElements).findIndex((el) => el.classList.contains(hoverClass || "active"))
                    : 0;
            let nextIndex = currentIndex;
            if (event.key === "ArrowDown") nextIndex++;
            if (event.key === "ArrowUp") nextIndex--;
            if (nextIndex >= focusableElements.length) nextIndex = 0;
            if (nextIndex < 0) nextIndex = focusableElements.length - 1;
            const nextElement = focusableElements[nextIndex];
            if (event.key === "Enter") {
                if (hideOnClickFocusableElement) setIsFocused(false);
                onClickEnter?.(nextElement);
                return;
            }
            if (focusMode === "focus") {
                focusableElements[nextIndex].focus();
            }
            if (focusMode === "hover") {
                focusableElements.forEach((el) => el.classList.remove(hoverClass || "active"));
                nextElement.classList.add(hoverClass || "active");
                onHoverFocusableElement?.(nextElement);
            }
        };
        const handleClick = (event: MouseEvent) => {
            if (!hideOnClickFocusableElement) return;

            const focusableElements = contentRef.current?.querySelectorAll<HTMLElement>(
                "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
            );
            if (!focusableElements) return;
            if (Array.from(focusableElements).some((el) => el.contains(event.target as Node))) {
                setIsFocused(false);
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        document.addEventListener("click", handleClick);

        return function cleanup() {
            document.removeEventListener("keydown", handleKeyDown);
            document.removeEventListener("click", handleClick);
        };
    }, [isOpen, hideOnClickFocusableElement, focusMode]);

    const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
        if (wrapperRef.current?.contains(event.relatedTarget as Node)) {
            return;
        }
        setIsFocused(false);
    };

    return (
        <Wrapper ref={wrapperRef} onBlur={handleBlur}>
            <Input onFocus={() => setIsFocused(true)} ref={inputRef}>
                {input}
            </Input>
            {isOpen && (
                <Content width={fitWidth ? inputWidth : undefined} ref={contentRef} tabIndex={0} onFocus={() => setIsFocused(true)}>
                    {useCustomStyles ? children : <Body>{children}</Body>}
                </Content>
            )}
        </Wrapper>
    );
};
